import React from 'react';
import { Tag, Tooltip } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { InhubTransportKpiCellResp } from '../graphql/queries/getInhubTransportKpiSummary';

interface Props {
    title: string;
    cell: InhubTransportKpiCellResp;
    rateLabelKey?: 'arrivalRate' | 'notArrivalRate';
    definitionTooltip?: React.ReactNode;
    hideWow?: boolean;
    onParcelDrilldown?: () => void;
    onBagDrilldown?: () => void;
}

const wowColorOf = (v: number): string =>
    v === 0 ? 'text-gray-600' : v > 0 ? 'text-green-600' : 'text-red-500';

const WowValue: React.FC<{ value: number }> = ({ value }) => {
    const sign = value > 0 ? '+' : '';
    const isZero = value === 0;
    return (
        <span className={`inline-flex items-center gap-0.5 ${wowColorOf(value)}`}>
            {!isZero && (value > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
            {sign}{value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
        </span>
    );
};

const formatRatePercent = (v: number | null): string =>
    typeof v === 'number' ? `${v.toFixed(1)}%` : '-';

const TransportKpiCard: React.FC<Props> = ({
    title, cell, rateLabelKey, definitionTooltip, hideWow, onParcelDrilldown, onBagDrilldown,
}) => {
    const { t } = useTranslation();
    const isFreshT1 = cell.freshness === 'T1';
    const I18N = 'wms.delivery.inhubDashboard';
    const parcelWow = cell.wowPercent ?? 0;
    const bagWow = cell.bagCountWowPercent ?? 0;
    const showRate = rateLabelKey === 'notArrivalRate';

    return (
        <div className="flex flex-col gap-2 rounded-lg shadow-sm border border-violet-200/70 px-4 py-3 h-full bg-violet-50">
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 font-semibold">
                    {title}
                    {definitionTooltip && (
                        <Tooltip title={definitionTooltip}>
                            <InfoCircleOutlined className="ml-1 text-gray-400" />
                        </Tooltip>
                    )}
                </span>
                <Tag color={isFreshT1 ? 'orange' : 'green'} className="m-0 text-xs" bordered={false}>
                    {isFreshT1 ? t(`${I18N}.badge.t1`) : t(`${I18N}.badge.realtime`)}
                </Tag>
            </div>

            <div className="grid grid-cols-[auto_auto_auto] items-baseline gap-x-2 w-fit">
                <div className="flex items-baseline gap-1.5">
                    {onParcelDrilldown && cell.parcelCount > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onParcelDrilldown(); }}
                            style={{ color: '#1890ff' }}
                            className="text-2xl font-semibold"
                        >
                            {Number(cell.parcelCount).toLocaleString()}
                        </a>
                    ) : (
                        <span className="text-2xl font-semibold text-gray-900">{Number(cell.parcelCount).toLocaleString()}</span>
                    )}
                    <span className="text-sm text-gray-600">{t(`${I18N}.transport.units.parcel`)}</span>
                </div>
                <span className="text-gray-500 text-xl font-bold justify-self-center">·</span>
                <div className="flex items-baseline gap-1.5">
                    {onBagDrilldown && cell.bagCount > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBagDrilldown(); }}
                            style={{ color: '#1890ff' }}
                            className="text-2xl font-semibold"
                        >
                            {Number(cell.bagCount).toLocaleString()}
                        </a>
                    ) : (
                        <span className="text-2xl font-semibold text-gray-900">{Number(cell.bagCount).toLocaleString()}</span>
                    )}
                    <span className="text-sm text-gray-600">{t(`${I18N}.transport.units.bag`)}</span>
                </div>

                {!hideWow && (
                    <>
                        <WowValue value={parcelWow} />
                        <span className="text-gray-500 text-base font-bold justify-self-center">·</span>
                        <div className="flex items-center gap-1 text-xs">
                            <WowValue value={bagWow} />
                            <span className="text-gray-500 ml-1">({t(`${I18N}.kpi.wowChange`)})</span>
                        </div>
                    </>
                )}

                {showRate && (
                    <>
                        <span className="text-xs text-red-500 font-medium">{formatRatePercent(cell.rate)}</span>
                        <span className="text-gray-500 text-base font-bold justify-self-center">·</span>
                        <div className="flex items-center gap-1 text-xs">
                            <span className="text-red-500 font-medium">{formatRatePercent(cell.bagRate)}</span>
                            <span className="text-gray-500 ml-1">({t(`${I18N}.transport.notArrivalRate`)})</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TransportKpiCard;
